import type { FC, ReactElement } from 'react';

import { useCms, useContent } from '@eneco/cms';
import RichText from '@eneco/components/RichText/RichText';
import { NotificationBox, Table as SparkyTable, Text } from '@eneco/sparky';

import type { TableCell, TableContent, TableRow } from './table-content.types';

/**
 * `Table` block for the CMS-agnostic lane, serving **both** providers.
 *
 * NOTE (schema gap): the bundle models `Table` as `{ title }` only — rows, sections and footers were
 * never modelled. `title` itself migrates fine (2 of the 7 stored blocks carry one; the rest had no
 * caption in Sitecore either), so this is narrower than the field-less `RichText`/`Hero` gap: the
 * caption survives, the table does not.
 *
 * Rows are still read from `content`, because the dataset is schemaless and may yet carry what the
 * schema omits (link `text` turned out to work exactly that way). Until then a block renders its
 * title if it has one, plus a dev-only notice, so an empty table reads as "not migrated" rather than
 * as a broken component.
 */
const Table: FC = () => {
  const content = useContent<TableContent>();
  const { headerRows, rows, sections, footerRows } = content;

  const hasRows = Boolean(
    headerRows?.length ||
      rows?.length ||
      footerRows?.length ||
      sections?.some(section => section.rows?.length || section.footerRows?.length),
  );

  if (!hasRows) {
    return (
      <>
        {content.title && <Text weight="bold">{content.title}</Text>}
        {process.env.NODE_ENV !== 'production' && (
          <NotificationBox
            isAlert={false}
            title="Table rows not migrated"
            text="The schema models this block as a title only."
          />
        )}
      </>
    );
  }

  return (
    <SparkyTable title={content.title}>
      {headerRows?.[0] && <SparkyTable.Header>{cells(headerRows[0])}</SparkyTable.Header>}

      {rows?.map((row, index) => <SparkyTable.Row key={index}>{cells(row)}</SparkyTable.Row>)}

      {sections?.map((section, index) =>
        section.rows?.length ? (
          <SparkyTable.Section key={index} isCollapsible={section.collapsible} title={section.title ?? ''}>
            {section.rows.map((row, rowIndex) => (
              <SparkyTable.Row key={rowIndex}>{cells(row)}</SparkyTable.Row>
            ))}
            {section.footerRows?.[0] && <SparkyTable.Row isFooter>{cells(section.footerRows[0])}</SparkyTable.Row>}
          </SparkyTable.Section>
        ) : null,
      )}

      {footerRows?.[0] && <SparkyTable.Row isFooter>{cells(footerRows[0])}</SparkyTable.Row>}
    </SparkyTable>
  );
};

/** A row's cells. Sparky's row/header/section children are typed as element arrays, so this returns
 * one rather than a `ReactNode`. */
const cells = (row: TableRow): ReactElement[] =>
  (row.columns ?? []).map((column, index) => <TableCellText key={index} cell={column} />);

const TableCellText: FC<{ cell: TableCell }> = ({ cell }) => {
  const { adapter } = useCms();
  const html = adapter.toRichText(cell.content);

  return <Text>{html ? <RichText html={html} /> : null}</Text>;
};

export default Table;
